import type { Middleware, UnknownAction } from "@reduxjs/toolkit";
import { REHYDRATE } from "redux-persist";

import { setMockDataEnabled } from "src/redux/slices/appSlice/AppSlice";
import { isProductionPassportcardOnboardingHost } from "src/utils/environmentHost";

type RehydrateAction = UnknownAction & {
  key?: string;
  payload?: Record<string, unknown>;
};

function stripMockFromRehydratePayload(
  key: string | undefined,
  payload: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!payload || key === undefined) {
    return payload;
  }

  if (key === "app" && payload.mockDataEnabled === true) {
    return { ...payload, mockDataEnabled: false };
  }

  if (key === "root") {
    const app = payload.app as Record<string, unknown> | undefined;
    if (app?.mockDataEnabled === true) {
      return {
        ...payload,
        app: { ...app, mockDataEnabled: false },
      };
    }
  }

  return payload;
}

/** Blocks mock on production hostname (including redux-persist rehydrate). */
export const productionMockGuardMiddleware: Middleware =
  (_store) => (next) => (action) => {
    if (!isProductionPassportcardOnboardingHost()) {
      return next(action);
    }

    if (setMockDataEnabled.match(action) && action.payload === true) {
      return next(setMockDataEnabled(false));
    }

    if (
      typeof action === "object" &&
      action !== null &&
      "type" in action &&
      (action as UnknownAction).type === REHYDRATE
    ) {
      const a = action as RehydrateAction;
      const nextPayload = stripMockFromRehydratePayload(a.key, a.payload);
      if (nextPayload !== a.payload) {
        return next({ ...a, payload: nextPayload });
      }
    }

    return next(action);
  };
